import type { Invoice, InvoiceFlag } from '@clockwise/clients/invoices';
import type { InvoiceLineKind } from '@clockwise/shared';
import { Link } from '@tanstack/react-router';
import { CircleAlert, CircleX, X } from 'lucide-react';
import { InvoiceFlagDetails } from '@/components/custom/invoice-flag-details';
import { cn } from '@/lib/utils';

export type InvoiceBlocker = {
  key: 'no-lines' | 'not-billable' | 'accounts' | 'no-email' | 'send-failed';
  message: string;
  /** What it stops. Null for a report rather than a gate (a failed send). */
  blocks: 'approval' | 'sending' | null;
};

/**
 * Everything standing between this invoice and its next step.
 *
 * Read from the FORM, not the saved row, so fixing a blocker clears it as it
 * is typed — the Save that makes it stick is by then the one filled button.
 * The approval rules mirror the server's (`InvoicesService.approve`): at least
 * one line, at least one service or expense line among them, and an address to
 * email it to. The server stays the authority; this only says it first.
 */
export function invoiceBlockers(s: {
  /** Null in create mode. */
  status: Invoice['status'] | null;
  lineKinds: readonly InvoiceLineKind[];
  /** 1-based numbers of lines without a complete account allocation. */
  unbookedLines?: readonly number[];
  email: string;
  sendError: string | null;
}): InvoiceBlocker[] {
  if (s.status === 'sent') return [];
  const out: InvoiceBlocker[] = [];
  if (s.status === 'draft') {
    if (s.lineKinds.length === 0) {
      out.push({ key: 'no-lines', message: 'No lines yet', blocks: 'approval' });
    } else if (!s.lineKinds.some((k) => k === 'service' || k === 'expense')) {
      out.push({
        key: 'not-billable',
        message:
          'Needs a service or expense line. Discounts and adjustments alone aren’t billable.',
        blocks: 'approval',
      });
    }
    const unbooked = s.unbookedLines ?? [];
    if (unbooked.length > 0) {
      out.push({
        key: 'accounts',
        message: `Line${unbooked.length === 1 ? '' : 's'} ${unbooked.join(', ')} ${
          unbooked.length === 1 ? 'needs' : 'need'
        } an account, and a split must add up to its line.`,
        blocks: 'approval',
      });
    }
  }
  if (s.email.trim() === '') {
    // Approval needs somewhere to send the invoice. An approved one can only
    // lack an address if it was removed around the lock, and then it is the
    // send that it stops.
    out.push({
      key: 'no-email',
      message: 'No billing email',
      blocks: s.status === 'approved' ? 'sending' : 'approval',
    });
  }
  // Recorded by the sender; stays until a send succeeds, or the invoice is
  // edited or rescheduled (which also lets the sender try again).
  if (s.sendError) {
    out.push({ key: 'send-failed', message: `Last send failed: ${s.sendError}`, blocks: null });
  }
  return out;
}

/**
 * What needs attention on this invoice, first thing under the header — and
 * nothing at all when nothing does. Two kinds, kept visually apart:
 *
 * - Blockers (amber): gates on the next step, each saying what it blocks and
 *   offering the fix.
 * - Review flags (red): what invoice generation observed. Not gates — the
 *   server approves a flagged invoice — and no dismiss, by design: the answer
 *   is to fix the invoice (which re-runs the review) or approve it anyway.
 *
 * A third, `error`, is what the last action (Save, Approve, Send…) was
 * refused with when the refusal names no field on the page. It sits here, in
 * place, rather than in a toast over the header's buttons.
 *
 * No "all clear" card: when nothing blocks, the header's filled button is the
 * next step, and a card saying so only repeated the totals and recipients
 * already on the page.
 */
export function InvoiceAttention({
  blockers,
  flags,
  priorInvoiceId,
  error = null,
  onDismissError,
  onAddLine,
  onAddEmail,
}: {
  blockers: readonly InvoiceBlocker[];
  flags: readonly InvoiceFlag[];
  priorInvoiceId: string | null;
  error?: string | null;
  onDismissError?: () => void;
  /** Absent when the viewer can't edit — the blocker still shows, unfixable
   *  from here. */
  onAddLine?: () => void;
  onAddEmail?: () => void;
}) {
  if (error === null && blockers.length === 0 && flags.length === 0) return null;

  return (
    <div className="flex flex-col gap-2">
      {error !== null ? (
        <div
          role="alert"
          className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 px-4 py-2.5 text-sm"
        >
          <CircleX className="mt-0.5 size-4 shrink-0 text-destructive" aria-hidden />
          <span className="flex-1 text-destructive">{error}</span>
          {onDismissError ? (
            <button
              type="button"
              onClick={onDismissError}
              aria-label="Dismiss"
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
      ) : null}
      {blockers.length > 0 ? (
        <section
          aria-label="Blocking this invoice"
          className="flex flex-col divide-y divide-amber-500/20 rounded-lg border border-amber-500/40 bg-amber-50/60 dark:bg-amber-950/20"
        >
          {blockers.map((b) => {
            const failed = b.key === 'send-failed';
            const Icon = failed ? CircleX : CircleAlert;
            const fix =
              b.key === 'no-lines' || b.key === 'not-billable'
                ? onAddLine && { label: 'Add line', run: onAddLine }
                : b.key === 'no-email'
                  ? onAddEmail && { label: 'Add email', run: onAddEmail }
                  : undefined;
            const note =
              b.blocks === 'approval'
                ? 'Blocks approval'
                : b.blocks === 'sending'
                  ? 'Blocks sending'
                  : null;
            return (
              <div
                key={b.key}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm"
              >
                <Icon
                  className={cn(
                    'size-4 shrink-0',
                    failed ? 'text-destructive' : 'text-amber-600 dark:text-amber-500',
                  )}
                  aria-hidden
                />
                <span className={failed ? 'text-destructive' : undefined}>{b.message}</span>
                {note ? <span className="text-muted-foreground text-xs">{note}</span> : null}
                {fix ? (
                  <button
                    type="button"
                    onClick={fix.run}
                    className="ml-auto font-medium underline underline-offset-2 hover:no-underline"
                  >
                    {fix.label}
                  </button>
                ) : null}
              </div>
            );
          })}
        </section>
      ) : null}
      {flags.length > 0 ? (
        <section
          aria-label="Review flags"
          className="flex flex-wrap items-start gap-x-6 gap-y-2 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3"
        >
          <InvoiceFlagDetails flags={flags} glyphClassName="text-destructive" className="flex-1" />
          {priorInvoiceId === null ? null : (
            <Link
              to="/invoices/$id"
              params={{ id: priorInvoiceId }}
              className="text-primary text-xs hover:underline"
            >
              Compare with the previous invoice
            </Link>
          )}
        </section>
      ) : null}
    </div>
  );
}
